/*
 * @Description:
 * @Author: Luke Z
 * @Date: 2021-03-18 11:34:56
 * @LastEditors: Luke Z
 * @LastEditTime: 2021-07-16 14:28:19
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesChannelContentEditor/SalesChannelEditorProductSearch/slice/index.ts
 * @LastEditTime: 2021-03-24 16:57:29
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesChannelContentEditor/SalesChannelEditorProductSearch/slice/index.ts
 */

import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';

import {
  EnumCollapseId,
  defaultCollapse,
} from 'src/pages/SalesChannelContentEditor/SalesChannelEditorProductSearch/common';
import {
  generateDefaultState as _generateDefaultState,
  onCollapseOpen as _onCollapseOpen,
  assData,
  defaultStatus,
  hasError,
} from 'src/pages/SalesChannelContentEditor/slice/reduxCommonFunctions';
import { ActionType } from 'src/types/common/common';

import arrayMove from 'array-move';
import {
  EnumReducers,
  getCollapseOpenStatus as _getCollapseOpenStatus,
  getCollapseValidationMsgDisplay as _getCollapseValidationMsgDisplay,
  getGeneralFeaturedProduct as _getGeneralFeaturedProduct,
  getGeneralMiniProgramPageInfo as _getGeneralMiniProgramPageInfo,
  getGeneralMiniProgramShare as _getGeneralMiniProgramShare,
  getHasModified as _getHasModified,
  getLoaded as _getLoaded,
  getSalesChannel as _getSalesChannel,
  getSalesChannelName as _getSalesChannelName,
  getState as _getState,
  getSubmitStamp as _getSubmitStamp,
  getTranslationState as _getTranslationState,
} from 'src/pages/SalesChannelContentEditor/slice/selectors';
import { msg } from 'src/tools/message';

import { convertDataFromServerToRedux } from 'src/pages/SalesChannelContentEditor/SalesChannelEditorProductSearch/tools';

import {
  getContentProductSearch,
  postContentProductSearch,
  postTranslateProductSearch,
} from '@/src/request/salesChannel/content';
import { getI18nFromGeneral } from '@/src/tools/tools';
import { isCurrentPrimary } from 'src/constrains/SalesChannelMultiLanguageSelect';

export { ActionType, EnumCollapseId };

export const fetchContentSearchPage = createAsyncThunk(
  'fetchSalesChannelContentProductSearchPage',
  async (data: any) => {
    const { uuid, params = {}, isClone } = data;
    const isPrimary = isCurrentPrimary();
    const response = await getContentProductSearch(uuid, params);
    return { response, isPrimary, isClone };
  },
);

export const submitContentHomepage = createAsyncThunk(
  'submitSalesChannelContentProductSearchPage',

  async ({ body, params }: any) => {
    const isPrimary = isCurrentPrimary();
    const response = await postContentProductSearch(body, params);
    return { response, isPrimary };
  },
);

export const translateContentProductSearchPage = createAsyncThunk(
  'translateSalesChannelContentProductSearchPage',
  async ({ uuid, translationEnabled, multiLanguageParams }: any) => {
    const response = await postTranslateProductSearch(
      uuid,
      translationEnabled,
      multiLanguageParams,
    );
    return { response, translationEnabled };
  },
);

const fixedCollapse = [
  EnumCollapseId.featuredProducts,
  EnumCollapseId.hotTags,
  EnumCollapseId.searchBar,
];

const _defaultState = _generateDefaultState(fixedCollapse);
const defaultState = {
  ..._defaultState,
  ...defaultCollapse(),
  enabled: false,
  hotTags: [],
  hotTagsTitle: '',
  searchBar: { placeholder: '' },
  featuredProducts: { titleTextColor: '' },
  translationEnabled: false,
  translationLoading: false,
  uuid: null,
  ...defaultStatus,
};

const salesChannelContentProductSearch = createSlice({
  name: 'salesChannelContentProductSearch',
  initialState: defaultState,
  reducers: {
    onCollapseOpen(state, action) {
      const { idArr = [] } = action.payload; //=>[id:string]
      _onCollapseOpen(state, idArr);
    },
    onHotTagsTitleChange(state, action) {
      state.hasModified = true;
      state.hotTagsTitle = action.payload;
    },
    onSearchBarPlaceholderChange(state, action) {
      state.hasModified = true;
      state.searchBar.placeholder = action.payload;
    },
    onFeaturedProductChange(state, action) {
      state.hasModified = true;
      state.featuredProducts.titleTextColor = action.payload;
    },
    onHotTagsChange(state, action) {
      state.hasModified = true;
      const { type, index, data } = action.payload;

      switch (type) {
        case ActionType.delete: {
          state.hotTags.splice(index, 1);
          return;
        }
        case ActionType.add: {
          state.hotTags.push(...data);
          return;
        }
        case ActionType.reorder: {
          const { from, to } = data || {};
          state.hotTags = arrayMove(state.hotTags, from, to);
          return;
        }
        case ActionType.modify: {
          state.hotTags[index] = data;
          return;
        }
        default:
          console.log('default');
          return;
      }
    },
    onErrorChange(state, action) {
      const { id, status } = action.payload;
      state.collapseErrorMsgDisplay[id].hasError = status;
    },
    onEnabledChange(state, action) {
      state.hasModified = true;
      state.enabled = action.payload;
    },
    onMiniProgramShareChange(state, action) {
      state.hasModified = true;
      state.miniProgramShare = {
        ...state.miniProgramShare,
        ...action.payload,
      };
    },
    validation(state) {
      if (state.enabled) {
        hasError(state, state.collapseErrorMsgDisplay || {});
      } else {
        state.isDataValid = true;
      }
    },
    resetData(state) {
      assData(state, defaultState);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchContentSearchPage.rejected, (state) => {
      state.loaded = true;
    });
    builder.addCase(fetchContentSearchPage.pending, (state) => {
      state.loaded = false;
    });
    builder.addCase(fetchContentSearchPage.fulfilled, (state, action) => {
      const { response, isClone } = action.payload;
      const _data = convertDataFromServerToRedux(response) || {};

      assData(state, _data);
      if (isClone) state.hasModified = true;
    });
    builder.addCase(submitContentHomepage.rejected, (state) => {
      state.loaded = true;
    });
    builder.addCase(submitContentHomepage.pending, (state) => {
      state.loaded = false;
    });
    builder.addCase(submitContentHomepage.fulfilled, (state, action) => {
      const { response } = action.payload;
      const _data = convertDataFromServerToRedux(response) || {};
      msg.success(getI18nFromGeneral('general.status.changessavesuccessfully'));
      assData(state, _data);
    });
    //translationEnable
    builder
      .addCase(translateContentProductSearchPage.pending, (state) => {
        state.translationLoading = true;
      })
      .addCase(translateContentProductSearchPage.fulfilled, (state, action) => {
        state.translationLoading = false;
        const { response, translationEnabled } = action.payload;
        if (response) {
          state.translationEnabled = translationEnabled;
          if (translationEnabled) state.hasModified = false;
        }
      })
      .addCase(translateContentProductSearchPage.rejected, (state) => {
        state.translationLoading = false;
      });
  },
});

export const {
  onHotTagsChange,
  onCollapseOpen,
  onHotTagsTitleChange,
  onSearchBarPlaceholderChange,
  onFeaturedProductChange,
  onErrorChange,
  onEnabledChange,
  validation,
  resetData,
  onMiniProgramShareChange,
} = salesChannelContentProductSearch.actions;
export const getHotTags = () => {
  return createSelector(
    (state: any) => state.salesChannelContent.productSearch.hotTags,
    (state: any) => state.salesChannelContent.productSearch.hotTagsTitle,

    (hotTags, title) => {
      return {
        items: hotTags,
        title,
      };
    },
  );
};
export const getCollapseOpenStatus = (id) => {
  return _getCollapseOpenStatus(EnumReducers.productSearch, id);
};

export const getContent = () => {
  return createSelector(
    (state: any) => state.salesChannelContent.productSearch.hotTags,
    (state: any) => state.salesChannelContent.productSearch.hotTagsTitle,
    (state: any) => state.salesChannelContent.productSearch.searchBar,
    (state: any) => state.salesChannelContent.productSearch.featuredProducts,
    (state: any) => state.salesChannelContent.productSearch.enabled,

    (hotTags, hotTagsTitle, searchBar, featuredProducts, enabled) => {
      return {
        enabled,
        featuredProducts,
        searchBar,
        hotTags: {
          items: hotTags,
          title: hotTagsTitle,
        },
      };
    },
  );
};

export const getState = () => {
  return _getState(EnumReducers.productSearch);
};

export const getCollapseValidationMsgDisplay = (id) => {
  return _getCollapseValidationMsgDisplay(EnumReducers.productSearch, id);
};

export const getHasModified = () => {
  return _getHasModified(EnumReducers.productSearch);
};

export const getSalesChannel = () => {
  return _getSalesChannel(EnumReducers.productSearch);
};

export const getSalesChannelName = () => {
  return _getSalesChannelName(EnumReducers.productSearch);
};

export const getGeneralFeaturedProduct = () => {
  return _getGeneralFeaturedProduct(EnumReducers.productSearch);
};

export const getLoaded = () => {
  return _getLoaded(EnumReducers.productSearch);
};

export const getSubmitStamp = () => {
  return _getSubmitStamp(EnumReducers.productSearch);
};

export const getGeneralMiniProgramShare = () => {
  return _getGeneralMiniProgramShare(EnumReducers.productSearch);
};

export const getGeneralMiniProgramPageInfo = () => {
  return _getGeneralMiniProgramPageInfo(EnumReducers.productSearch);
};
export const getTranslationState = () => {
  return _getTranslationState(EnumReducers.productSearch);
};

export default salesChannelContentProductSearch.reducer;
